import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterContentChecked,
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseSiteService } from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import {
  DEFAULT_ORDER_HISTORY_TRANSLATIONS,
  DEFAULT_ORDER_HISTORY_TYPES,
  INVOICE_ADDITIONAL_ORDER_HISTORY_TYPES,
  OrderHistoryTranslatables,
  PLANNED_ORDER_HISTORY_TYPE,
} from 'src/app/model/order-history.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { OrderHistoryService } from 'src/app/service/orders/order-history.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { APP_DATE_FORMATS, AppDateAdapter } from '../format-datepicker';

@Component({
  selector: 'app-order-history-filter',
  templateUrl: './order-history-filter.component.html',
  styleUrls: ['./order-history-filter.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderHistoryFilterComponent implements OnInit, OnDestroy, AfterContentChecked, AfterViewChecked {
  totalNumberOfResults = 0;
  filterType = 0;
  features: any = {};
  backOrderFeature = true;
  fromDateBE: any;
  toDateBE: any;
  hasError = false;
  disableOrderHistoryBtn: boolean;
  disableBackOrderBtn: boolean;
  isDesktop: any;
  isMobile: any;
  disableInvoiceBtn: boolean;
  isGetFeaturesDone: boolean;
  translatables: OrderHistoryTranslatables = DEFAULT_ORDER_HISTORY_TRANSLATIONS;
  orderHistoryTypes = cloneDeep(DEFAULT_ORDER_HISTORY_TYPES);

  // Fields
  selected = 'orderNumber';
  searchField: any;
  backorder = false;
  invoice = false;
  statusOpen = false;
  statusClosed = false;
  statusBackOrder = false;
  fromDateInput: any;
  fromDate: string;
  toDateInput: any;
  toDate: string;

  // Subscriptions
  selectedFilterSubscription$: Subscription;
  totalOrdersSubscription$: Subscription;
  orderHistoryFilterSubscription$: Subscription;

  // Observables
  baseSite$: Observable<string> = this.baseService.getActive();
  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();
  showPlannedOrder$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.PLANNED_ORDER)
    )
  );
  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'orders.articlesInBackOrder',
        'orders.backOrder',
      ])
    )
  );

  constructor(
    private cdr: ChangeDetectorRef,
    private orderHistoryService: OrderHistoryService,
    private deviceService: DeviceDetectorService,
    private datePipe: DatePipe,
    private http: HttpClient,
    private baseService: BaseSiteService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private countryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private customTranslationService: CustomTranslationService,
    private breakpointService: BreakpointService,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private componentService: ComponentService,
    private router: Router
  ) {
    this.http
      .get(`../../../../../../public/features/ORDER_HISTORY_FEATURES.json`)
      .pipe(take(1))
      .subscribe((resp: any) => {
        if (resp[this.baseSiteId] && resp[this.baseSiteId].hasOwnProperty('backOrderFeature'))
          this.backOrderFeature = resp[this.baseSiteId].backOrderFeature;
      });

    // get customized order history features
    this.orderHistoryService
      .getFeatures()
      .pipe(take(1))
      .subscribe((features: any) => {
        this.features = { ...features, ...this.features };
        this.isGetFeaturesDone = true;
        this.addFeatures();
        this.setTranslatables();
        this.disableSelectedOrderTypes();
      });
  }

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  ngOnInit() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.selectedFilterSubscription$ = this.orderHistoryService.selectedFilter$.subscribe((status) => {
      this.filterType = status;
    });

    if (this.activatedRoute.snapshot.queryParamMap.get('view') === 'backorder') {
      this.backorder = true;
      this.storageService.setSessionStorageItem('orderState', 'backorder');
      this.orderHistoryService.getOrderHistory$.next(['backorder', false, true]);
    } else if (this.activatedRoute.snapshot.queryParamMap.get('view') === 'invoice') {
      this.invoice = true;
      this.storageService.setSessionStorageItem('orderState', 'invoice');
    }
    if (this.activatedRoute.snapshot.queryParamMap.get('item')) {
      this.selected = 'itemNumber';
      this.searchField = this.activatedRoute.snapshot.queryParamMap.get('item');
    }

    this.totalOrdersSubscription$ = this.orderHistoryService.totalOrders$.subscribe((total) => {
      this.totalNumberOfResults = total;
      this.cdr.detectChanges();
    });

    this.orderHistoryFilterSubscription$ = this.componentService.orderHistoryFilter$.subscribe((data) => {
      if (data) {
        this.selected = data.searchBy || this.selected;
        this.searchField = data.searchField;
        this.backorder = data.type === 'backorder';
        this.invoice = data.type === 'invoice';
        this.statusOpen = data.orderStatus.includes(1);
        this.statusClosed = data.orderStatus.includes(2);
        this.statusBackOrder = data.orderStatus.includes(3);
        this.fromDateInput = moment(data.fromDate).isValid() ? moment(data.fromDate) : undefined;
        this.toDateInput = moment(data.toDate).isValid() ? moment(data.toDate) : undefined;
      }
    });

    this.getIsCheckoutFeaturesEnabled();
  }

  getOrderStateValue() {
    //delay to give time to sessionstorage to set its value before reading this
    setTimeout(() => {
      let sessionStoreValue = this.sessionStore;
      switch (sessionStoreValue) {
        case 'orders':
          {
            this.backorder = false;
            this.invoice = false;
          }
          break;
        case 'backorder':
          {
            this.backorder = true;
            this.invoice = false;
          }
          break;
        case 'invoice':
          {
            this.backorder = false;
            this.invoice = true;
          }
          break;
        default:
          break;
      }
      this.cdr.detectChanges();
    });
  }

  ngAfterViewChecked() {
    this.getOrderStateValue();
    this.disableBtn();
    this.cdr.detectChanges();
  }

  clearSearchField() {
    this.searchField = '';
  }

  onFilterSelection() {
    this.orderHistoryService.setFilterSearchBy(this.selected);
    this.orderHistoryService.setOrderHistoryFilterObservable({ searchBy: this.selected });
  }

  newSearch($event: any) {
    if (typeof $event !== 'undefined') {
      if (typeof $event.target.value !== 'undefined') {
        this.orderHistoryService.setFilterSearchBy(this.selected);
        if ($event.target.value.length >= 4) {
          this.orderHistoryService.setFilterSearch($event.target.value);
        }
        if ($event.target.value.length === 0) {
          this.orderHistoryService.setFilterSearch('');
        }
      }
      this.orderHistoryService.setOrderHistoryFilterObservable({ searchField: $event.target.value });
    }
  }

  onChangeDate($event: any, type: string) {
    if (type === 'fdate') {
      this.fromDate = this.datePipe.transform($event.value.toString(), 'dd-MM-yyyy');
      const date = moment.utc($event.value.toISOString()).local();
      this.fromDateBE = date.format();
      if (this.fromDateBE > this.toDateBE) {
        this.hasError = true;
      } else {
        this.hasError = false;
      }

      this.orderHistoryService.setFromDate(this.fromDateBE);
    } else {
      this.toDate = this.datePipe.transform($event.value.toString(), 'dd/MM/yyyy');
      const date = moment.utc($event.value.toISOString()).local();
      date.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
      this.toDateBE = date.format();
      if (this.fromDateBE > this.toDateBE) {
        this.hasError = true;
      } else {
        this.hasError = false;
      }
      this.orderHistoryService.setToDate(this.toDateBE);
    }
  }

  onClickStatus($event: any, status: string) {
    switch (status) {
      case 'open':
        {
          this.statusOpen = !this.statusOpen;
          if (this.statusOpen) {
            this.orderHistoryService.setOrderStatuses(1);
          } else {
            this.orderHistoryService.removeOrderStatuses(1);
          }
        }
        break;
      case 'closed':
        {
          this.statusClosed = !this.statusClosed;
          if (this.statusClosed) {
            this.orderHistoryService.setOrderStatuses(2);
          } else {
            this.orderHistoryService.removeOrderStatuses(2);
          }
        }
        break;
      case 'backorder':
        {
          this.statusBackOrder = !this.statusBackOrder;
          if (this.statusBackOrder) {
            this.orderHistoryService.setOrderStatuses(3);
          } else {
            this.orderHistoryService.removeOrderStatuses(3);
          }
        }
        break;
      default:
        break;
    }
  }

  onClickType($event: any, index: number) {
    if (index !== null) this.orderHistoryTypes[index].selected = !this.orderHistoryTypes[index].selected;
    this.orderHistoryService.setOrderTypes(this.orderHistoryTypes);
  }

  onClearFilters() {
    this.searchField = '';
    this.fromDateInput = undefined;
    this.toDateInput = undefined;
    this.fromDateBE = undefined;
    this.toDateBE = undefined;
    this.statusOpen = false;
    this.statusClosed = false;
    this.statusBackOrder = false;
    this.hasError = false;
    this.filterType = 0;
    this.selected = 'orderNumber';
    this.orderHistoryService.setSelectedFilter(this.filterType);
    this.orderHistoryService.setClearFilters(true);
    this.orderHistoryService.setFilterSearch('');
    this.orderHistoryService.setOrderHistoryFilterObservable({ searchBy: 'orderNumber', searchField: '' });
    // Reset order types
    this.orderHistoryTypes.forEach((type: any) => {
      type.selected = false;
    });
  }
  get sessionStore() {
    return this.storageService.getSessionStorageItem('orderState');
  }

  updateState($event: any, $state: any, status: any) {
    // Update query params
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    queryParams['view'] = null;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });

    this.storageService.setSessionStorageItem('orderState', $state);

    switch ($state) {
      case 'orders':
        {
          this.backorder = false;
          this.invoice = false;
        }
        break;
      case 'backorder':
        {
          this.backorder = true;
          this.invoice = false;
          this.statusOpen = false;
          this.statusClosed = false;
          this.statusBackOrder = false;
          this.orderHistoryService.removeOrderStatuses(1);
          this.orderHistoryService.removeOrderStatuses(2);
          this.orderHistoryService.removeOrderStatuses(3);
        }
        break;
      case 'invoice':
        {
          this.invoice = true;
          this.backorder = false;
          this.statusOpen = false;
          this.statusClosed = false;
          this.statusBackOrder = false;
          this.orderHistoryService.removeOrderStatuses(1);
          this.orderHistoryService.removeOrderStatuses(2);
          this.orderHistoryService.removeOrderStatuses(3);
        }
        break;
      default:
        break;
    }

    this.disableSelectedOrderTypes();
    this.orderHistoryService.setOrderTypes(this.orderHistoryTypes);
    this.orderHistoryService.getOrderHistory$.next([$state, false, true]);
    this.setTranslatables();
  }

  onClickOpenFilter(status: number) {
    switch (status) {
      case 1:
        this.filterType = 1;
        break;
      case 2:
        this.filterType = 2;
        break;
      case 3:
        this.filterType = 3;
        break;
      default:
        this.filterType = 0;
    }

    this.orderHistoryService.setSelectedFilter(this.filterType);
  }

  get isNonDesktop$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => breakpoint !== BREAKPOINT.xl));
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  get isDesktop$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => breakpoint === BREAKPOINT.xl));
  }

  disableBtn(): any {
    if (this.sessionStore && this.sessionStore !== undefined) {
      switch (this.sessionStore) {
        case 'orders':
          {
            this.disableOrderHistoryBtn = true;
            this.disableBackOrderBtn = false;
            this.disableInvoiceBtn = false;
          }
          break;
        case 'backorder':
          {
            this.disableOrderHistoryBtn = false;
            this.disableBackOrderBtn = true;
            this.disableInvoiceBtn = false;
          }
          break;
        case 'invoice': {
          this.disableOrderHistoryBtn = false;
          this.disableBackOrderBtn = false;
          this.disableInvoiceBtn = true;
        }
      }
    }
  }

  /* GREY OUT ORDER STATUS BUTTONS */
  disableOrderStatus(status: string): boolean {
    const orderStatusesSelected: any = {
      open: this.statusOpen,
      closed: this.statusClosed,
      backorder: this.statusBackOrder,
    };

    const selectedLength = Object.values(orderStatusesSelected).filter((value) => value === true).length;

    // Disable unselected status and has selected 2 other statuses
    const selectedTwoStatuses = orderStatusesSelected[status] === false && selectedLength === 2;
    // Disable order status when view is backorder or invoice
    const disableOnOrderView = this.backorder || this.invoice;

    return (this.features.invoiceHistoryEnabled && selectedTwoStatuses) || disableOnOrderView;
  }

  /* GREY OUT ORDER TYPE BUTTONS */
  disableSelectedOrderTypes(): void {
    if (this.sessionStore && this.sessionStore !== undefined) {
      let disableOrderTypes: string[];

      switch (this.sessionStore) {
        case 'orders':
          {
            disableOrderTypes = ['DO', 'CN'];
          }
          break;
        case 'backorder':
          {
            if (this.features.invoiceHistoryEnabled) {
              disableOrderTypes = ['CO', 'DO', 'CN'];
            } else {
              disableOrderTypes = ['SO', 'PO'];
            }
          }
          break;
        case 'invoice':
          {
            disableOrderTypes = [];
          }
          break;
        default:
          break;
      }

      this.orderHistoryTypes.forEach((orderType) => {
        if (disableOrderTypes.includes(orderType.type)) {
          orderType.disabled = true;
          orderType.selected = false;
        } else {
          orderType.disabled = false;
        }
      });
    }
  }

  /* DYNAMIC TRANSLATIONS */
  setTranslatables(): void {
    const features = this.features;
    const isBackorder = this.backorder;
    const isInvoice = this.invoice;
    const isOrders = !this.backorder && !this.invoice;

    if (features.invoiceHistoryEnabled) {
      this.translatables = {
        orderViewBackorderBtn: { key: 'orders.itemsInBackOrder' },
        orderDate: isInvoice ? { key: 'orders.invoiceDate' } : { key: 'orders.date' },
        orderDateFrom: isInvoice ? { key: 'orders.invoiceHistory.from' } : { key: 'orders.from' },
        orderDateTo: isInvoice ? { key: 'orders.invoiceHistory.to' } : { key: 'orders.to' },
      };
    }

    if ((features.showBackorderRequestDate && isBackorder) || (features.showOrderRequestDate && isOrders)) {
      this.translatables.orderDate = { key: 'orders.dateRequest' };
    }
  }

  addFeatures(): void {
    if (this.features.invoiceHistoryEnabled) {
      /* ADD ADDITIONAL ORDER TYPES */
      cloneDeep(INVOICE_ADDITIONAL_ORDER_HISTORY_TYPES).forEach((type) => {
        if (!this.orderHistoryTypes.includes(type)) {
          this.orderHistoryTypes.push(type);
        }
      });
    }
  }

  getIsCheckoutFeaturesEnabled(): void {
    this.showPlannedOrder$.pipe(take(1)).subscribe((showPlannedOrder: boolean) => {
      if (showPlannedOrder) {
        cloneDeep(PLANNED_ORDER_HISTORY_TYPE).forEach((type) => {
          if (!this.orderHistoryTypes.includes(type)) {
            this.orderHistoryTypes.push(type);
          }
        });
      }
    });
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.storageService.removeSessionStorageItem('orderState');
    this.orderHistoryService.clearOrderHistoryFilterObservable();
    this.totalOrdersSubscription$?.unsubscribe();
    this.orderHistoryFilterSubscription$?.unsubscribe();
  }
}
